import { QueryClientProvider } from "@tanstack/react-query";
import { IframePidgetProvider, useIframePidget } from "@workspace/utils-react";
import React, { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import { IframeContext } from "./context/IframeContext";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import App from "./App";
import { AuthError } from "./components/Layout/AuthError/AuthError";
import { CookieConsentBanner } from "./components/CookieConsent/CookieConsentBanner";
import { PageLoader } from "./components/Layout/PageLoader/PageLoader";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import { DiaryContextProvider } from "./context/DiaryContext";
import { SpaceOptimiserProvider } from "./context/SpaceOptimiserContext";
import { UserContextProvider } from "./context/UserContext";
import "./index.css";

import { polyfill } from "mobile-drag-drop";
import { scrollBehaviourDragImageTranslateOverride } from "mobile-drag-drop/scroll-behaviour";
import { AppContextProvider } from "./context/AppContext";
import { CollinsUserContextProvider } from "./context/CollinsUserContext";
import { ConfigCatFlagsProvider } from "./context/ConfigCatFlagsContext";
import { CustomerContextProvider } from "./context/CustomerContext";
import { DeviceContextProvider } from "./context/DeviceContext";
import { FeatureFlagContextProvider } from "./context/FeatureFlagsContext";
import { InfoModalContextProvider } from "./context/InfoModalContext";
import { LoadingStatusProvider } from "./context/LoadingStatusContext";
import { NotificationContextProvider } from "./context/NotificationsContext";
import { UIContextProvider } from "./context/UIContext";
import { UserPreferencesContextProvider } from "./context/UserPreferencesContext";
import { i18nInstance } from "./i18n/createi18nInstance";
import { I18nLanguageSync } from "./i18n/I18nLanguageSync";
import { dmnIdentity } from "./utils/dmnIdentity";
import { queryClient } from "./config/queryClient";

const useragent = window.navigator.userAgent.toLowerCase();
const isiPad =
  useragent.indexOf("ipad") > -1 ||
  (useragent.indexOf("macintosh") > -1 && "ontouchend" in document);
// GridV2 diary drag is Pointer Events, not HTML5 DnD. The iPad polyfill
// still installs document touch listeners that Array.join a drag-image
// clone; on iOS 26.2 WKWebView that SIGBUS/OOM'd WebContent when unlocking
// the grid and moving a booking. Native sidebar HTML5 DnD stays desktop-only.
if (typeof __REACT_NATIVE_BUILD__ === "undefined" || !__REACT_NATIVE_BUILD__) {
  const usePolyfill = polyfill({
    dragImageTranslateOverride:
      scrollBehaviourDragImageTranslateOverride || isiPad,
    tryFindDraggableTarget: (e: TouchEvent) => {
      /* This is to ensure that the touchevent drag and drop polyfill only actions on items we choose.
         at the moment we bind data-drag="true" to a div. This only works with HTMLElements atm.
         If in future we wish buttons etc to be included amend here */
      const target = e.target as HTMLElement;
      if (target?.dataset?.drag === "true") {
        return target;
      } else {
        return undefined;
      }
    },
    forceApply: isiPad
  });
  if (usePolyfill) {
    // Required by mobile-drag-drop polyfill on iOS Safari 10+.
    // The polyfill calls preventDefault() on touchmove during drag operations,
    // but iOS Safari defaults window/document touch listeners to passive.
    // This global non-passive listener overrides that default so the polyfill
    // can cancel scroll during drag. Scoping to individual drag containers is
    // not viable because targets are dynamic and the passive default applies
    // at the document/window level. See polyfill issues #77 and #124.
    window.addEventListener("touchmove", function () {}, { passive: false });
  }
}

type AuthStatus = "checking" | "authenticated" | "failed";

// Upper bound on the initial token check. Without it a token retrieval that
// never settles (e.g. a silent renew that hangs) would leave the app-shell
// skeleton spinning indefinitely. On timeout we settle to "failed" so the
// AuthError retry is shown instead of an endless loading state.
const AUTH_CHECK_TIMEOUT_MS = 20000;

export function RootApp() {
  const iframePidget = useIframePidget();
  const isInIframe = !!iframePidget;

  const [authStatus, setAuthStatus] = React.useState<AuthStatus>("checking");
  useEffect(() => {
    let settled = false;

    const settle = (status: AuthStatus) => {
      if (settled) {
        return;
      }
      settled = true;
      setAuthStatus(status);
    };

    const timeoutId = setTimeout(() => settle("failed"), AUTH_CHECK_TIMEOUT_MS);

    (async () => {
      try {
        const token = await dmnIdentity.getAccessToken();
        settle(token ? "authenticated" : "failed");
      } catch {
        settle("failed");
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => {
      settled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  if (authStatus === "checking") {
    // Paint the app-shell skeleton (the same one used as the router's
    // Suspense fallback) during the auth/token check rather than a blank
    // screen, so the first meaningful paint happens immediately and LCP
    // improves. The real UI renders once a token is present.
    return <PageLoader />;
  }

  if (authStatus === "failed") {
    // The token check has settled without a valid token (expired,
    // unauthenticated, or an error). Surface an actionable message with a
    // retry rather than leaving the skeleton spinning indefinitely.
    return <AuthError onRetry={() => window.location.reload()} />;
  }

  const appContent = (
    <I18nextProvider i18n={i18nInstance}>
      <CookieConsentProvider>
        <IframeContext.Provider value={isInIframe}>
          <Router>
            <QueryClientProvider client={queryClient}>
              <LoadingStatusProvider>
                <InfoModalContextProvider>
                  <DeviceContextProvider>
                    <UIContextProvider>
                      <NotificationContextProvider>
                        <UserContextProvider>
                          <CollinsUserContextProvider>
                            <FeatureFlagContextProvider>
                              <ConfigCatFlagsProvider>
                                <UserPreferencesContextProvider>
                                  <I18nLanguageSync />
                                  <DiaryContextProvider>
                                    <SpaceOptimiserProvider>
                                      <CustomerContextProvider>
                                        <AppContextProvider>
                                          <App />
                                          <CookieConsentBanner />
                                        </AppContextProvider>
                                      </CustomerContextProvider>
                                    </SpaceOptimiserProvider>
                                  </DiaryContextProvider>
                                </UserPreferencesContextProvider>
                              </ConfigCatFlagsProvider>
                            </FeatureFlagContextProvider>
                          </CollinsUserContextProvider>
                        </UserContextProvider>
                      </NotificationContextProvider>
                    </UIContextProvider>
                  </DeviceContextProvider>
                </InfoModalContextProvider>
              </LoadingStatusProvider>
            </QueryClientProvider>
          </Router>
        </IframeContext.Provider>
      </CookieConsentProvider>
    </I18nextProvider>
  );

  if (iframePidget) {
    return (
      <IframePidgetProvider iframePidget={iframePidget}>
        {appContent}
      </IframePidgetProvider>
    );
  }

  return appContent;
}

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <RootApp />
    </React.StrictMode>
  );
}
